
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence, PanInfo, useAnimation } from "framer-motion";
import { cn } from "@/lib/utils";
import { Globe, Mic, WifiOff, Check, Moon, ChevronRight, ChevronLeft, Lock } from "lucide-react";

// --- Configuration (Traditional Chinese) ---

const SLIDES = [
  {
    id: "intro",
    title: "teleprompterShowcase.slides.intro.title",
    subtitle: "teleprompterShowcase.slides.intro.subtitle",
    desc: "teleprompterShowcase.slides.intro.desc",
    theme: "light",
  },
  {
    id: "language",
    title: "teleprompterShowcase.slides.language.title",
    subtitle: "teleprompterShowcase.slides.language.subtitle",
    desc: "teleprompterShowcase.slides.language.desc",
    theme: "light",
  },
  {
    id: "speed",
    title: "teleprompterShowcase.slides.speed.title",
    subtitle: "teleprompterShowcase.slides.speed.subtitle",
    desc: "teleprompterShowcase.slides.speed.desc",
    theme: "light",
  },
  {
    id: "darkmode",
    title: "teleprompterShowcase.slides.darkmode.title",
    subtitle: "teleprompterShowcase.slides.darkmode.subtitle",
    desc: "teleprompterShowcase.slides.darkmode.desc",
    theme: "dark", // Trigger screen inversion & background change
  },
  {
    id: "offline",
    title: "teleprompterShowcase.slides.offline.title",
    subtitle: "teleprompterShowcase.slides.offline.subtitle",
    desc: "teleprompterShowcase.slides.offline.desc",
    theme: "light",
  },
];

// DEMO_TEXT removed - using translation keys

// --- Main Component ---

export const TeleprompterShowcase = () => {
    const { t } = useTranslation();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [selectedLang, setSelectedLang] = useState(1); // 0=English, 1=繁體中文, 2=日本語
    const [speedValue, setSpeedValue] = useState(50); // 0-100 slider value
    const [darkModeOn, setDarkModeOn] = useState(true);
    const controls = useAnimation();
    const containerRef = useRef<HTMLDivElement>(null);

    // Transitions based on theme - combine slide theme with user toggle
    // On darkmode slide, respect user's toggle. On other slides, use slide theme.
    const isDark = currentSlide === 3 ? darkModeOn : SLIDES[currentSlide].theme === "dark";

    const nextSlide = () => {
        if (currentSlide < SLIDES.length - 1) setCurrentSlide(c => c + 1);
    };

    const prevSlide = () => {
        if (currentSlide > 0) setCurrentSlide(c => c - 1);
    };

    const goToSlide = (index: number) => {
        setCurrentSlide(index);
    };

    // Swipe Handler
    const onDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
        const threshold = 50;
        if (info.offset.x < -threshold) nextSlide();
        else if (info.offset.x > threshold) prevSlide();
    };

    return (
        <div 
            className="w-full flex flex-col items-center justify-center overflow-hidden mt-48 md:mt-64 py-12 md:py-20 px-4"
        >
            {/* 1. Main Content Grid */}
            <div className="relative w-full max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 z-10">
                
                {/* Left: Text Content (Desktop) / Bottom (Mobile) */}
                <div className="order-2 md:order-1 flex flex-col justify-start space-y-6 md:pl-12 md:pt-24 text-center md:text-left relative pb-20 md:pb-0">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentSlide}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ duration: 0.4 }}
                        >
                            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold tracking-tight text-gray-900 transition-colors">
                                {t(SLIDES[currentSlide].title)}
                            </h2>
                            <h3 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold tracking-tight text-primary text-left mt-4 transition-colors">
                                {t(SLIDES[currentSlide].subtitle)}
                            </h3>
                            <p className="text-lg md:text-xl font-medium max-w-md md:mx-0 leading-relaxed text-gray-600 mt-12 transition-colors">
                                {t(SLIDES[currentSlide].desc)}
                            </p>
                        </motion.div>
                    </AnimatePresence>



                    {/* Navigation Dots */}
                    <div className="absolute bottom-12 md:bottom-0 left-1/2 -translate-x-1/2 md:left-12 md:translate-x-0 flex items-center gap-3">
                        {SLIDES.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => goToSlide(i)}
                                className={cn(
                                    "w-2.5 h-2.5 rounded-full transition-all duration-300",
                                    currentSlide === i 
                                        ? "w-8 bg-black" 
                                        : "bg-gray-300 hover:bg-gray-400"
                                )}
                                aria-label={`Go to slide ${i + 1}`}
                            />
                        ))}
                    </div>
                </div>

                {/* Right: Device (Desktop) / Top (Mobile) */}
                <div 
                    className="order-1 md:order-2 flex justify-center items-center relative"
                >
                    {/* The Device */}
                    <motion.div 
                        className={cn(
                            "relative w-[70vw] md:w-[24vw] max-w-[340px] aspect-[9/18] rounded-[2.5rem] shadow-2xl overflow-hidden transition-all duration-700 border-[7px]",
                            isDark ? "bg-black border-zinc-800 shadow-[0_0_50px_-12px_rgba(0,0,0,0.3)]" : "bg-white border-white shadow-xl"
                        )}
                        layout
                    >
                         {/* Internal Screen */}
                         <div className={cn(
                             "absolute inset-0 flex flex-col transition-colors duration-700",
                             isDark ? "bg-zinc-950" : "bg-zinc-50"
                         )}>
                            
                             {/* StatusBar */}
                             <div className="h-8 w-full flex items-center justify-between px-5 pt-3 z-20 opacity-50">
                                 <span className={cn("text-[10px] font-bold", isDark ? "text-white" : "text-black")}>9:41</span>
                                 <div className="flex gap-1">
                                    <div className={cn("w-3 h-1.5 rounded-sm border", isDark ? "border-white" : "border-black")} />
                                 </div>
                             </div>

                             {/* Content Stage */}
                             <div className="flex-1 relative overflow-hidden flex flex-col items-center justify-center p-6">
                                 
                                 {/* Teleprompter Text */}
                                 <DeviceTextContent slideId={SLIDES[currentSlide].id} isDark={isDark} speedValue={speedValue} selectedLang={selectedLang} />

                                 {/* UI Overlays */}
                                 <div className="absolute inset-x-0 bottom-6 flex justify-center z-30 px-3">
                                     <DeviceUIOverlay 
                                         slideId={SLIDES[currentSlide].id} 
                                         isDark={isDark} 
                                         selectedLang={selectedLang}
                                         onSelectLang={setSelectedLang}
                                         speedValue={speedValue}
                                         onSpeedChange={setSpeedValue}
                                         darkModeOn={darkModeOn}
                                         onToggleDarkMode={() => setDarkModeOn(!darkModeOn)}
                                     />
                                 </div>
                             </div>
                         </div>
                    </motion.div>
                </div>
            </div>


            
        </div>
    );
};


interface DeviceTextContentProps {
    slideId: string;
    isDark: boolean;
    speedValue: number;
    selectedLang: number;
}

const DeviceTextContent = ({ slideId, isDark, speedValue, selectedLang }: DeviceTextContentProps) => {
    const { t } = useTranslation();
    const [offset, setOffset] = useState(0);
    
    // Speed controlled by slider: map 0-100 to 0.3-4.0
    const speed = slideId === 'speed' ? 0.3 + (speedValue / 100) * 3.7 : 0.5;
    
    useEffect(() => {
        let reqId: number;
        const animate = () => {
             setOffset(p => (p + speed) % 600);
             reqId = requestAnimationFrame(animate);
        }
        reqId = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(reqId);
    }, [speed]);

    // Language Demo - use selectedLang on language slide
    const langs = ["Welcome", "歡迎使用", "ようこそ"];
    const displayLangIndex = slideId === 'language' ? selectedLang : 1; // Default to Chinese

    return (
        <div 
            className="w-full h-full relative overflow-hidden"
            style={{ 
                maskImage: "linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)",
                WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)"
            }}
        >
             {/* Content */}
             <div 
                className="w-full text-center space-y-8 pt-12 will-change-transform"
                style={{ transform: `translateY(-${offset}px)` }}
             >
                 {[...Array(3)].map((_, groupIdx) => (
                    <div key={groupIdx} className="space-y-8">
                        {(() => {
                            const demoText = t("teleprompterShowcase.demoText", { returnObjects: true });
                            const lines = Array.isArray(demoText) ? demoText : ["Vesper", "Professional", "Teleprompter", "Creators", "Simple"];
                            return lines.map((line, i) => (
                            <p 
                                key={`${groupIdx}-${i}`} 
                                className={cn(
                                    "text-2xl font-bold tracking-tight transition-all duration-300",
                                    isDark ? "text-white" : "text-zinc-900",
                                    slideId === 'language' && i === 0 ? "text-primary" : ""
                                )}
                            >
                                {slideId === 'language' && i === 0 ? langs[displayLangIndex] : line}
                            </p>
                            ));
                        })()}
                    </div>
                ))}
             </div>
        </div>
    )
}

interface DeviceUIOverlayProps {
    slideId: string;
    isDark: boolean;
    selectedLang: number;
    onSelectLang: (lang: number) => void;
    speedValue: number;
    onSpeedChange: (value: number) => void;
    darkModeOn: boolean;
    onToggleDarkMode: () => void;
}

const DeviceUIOverlay = ({ 
    slideId, 
    isDark, 
    selectedLang, 
    onSelectLang, 
    speedValue, 
    onSpeedChange, 
    darkModeOn, 
    onToggleDarkMode 
}: DeviceUIOverlayProps) => {
    const LANGUAGES = ["English", "繁體中文", "日本語"];

    return (
        <AnimatePresence mode="wait">
            
            {/* Lang Menu */}
            {slideId === 'language' && (
                <motion.div 
                    key="lang"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 20, opacity: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white/95 backdrop-blur shadow-lg border border-black/5 rounded-xl p-1.5 w-full max-w-[160px]"
                >
                    <div className="space-y-0.5">
                        {LANGUAGES.map((l, i) => (
                             <button 
                                key={l} 
                                onClick={() => onSelectLang(i)}
                                className={cn(
                                    "w-full text-[10px] px-2 py-1.5 rounded-md flex items-center justify-between transition-colors cursor-pointer hover:bg-primary/5",
                                    selectedLang === i ? "bg-primary/10 text-primary font-bold" : "text-gray-500"
                                )}
                             >
                                 {l}
                                 {selectedLang === i && <Check className="w-2.5 h-2.5" />}
                             </button>
                        ))}
                    </div>
                </motion.div>
            )}

            {/* Speed Control with draggable slider */}
            {slideId === 'speed' && (
                 <motion.div 
                    key="speed"
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="bg-zinc-900/90 backdrop-blur text-white shadow-xl rounded-full px-3 py-2 w-full max-w-[180px] flex items-center gap-2"
                    onPointerDown={(e) => e.stopPropagation()}
                >
                    <span className="text-[8px] font-bold">1x</span>
                    <div 
                        className="h-2 flex-1 bg-white/20 rounded-full overflow-hidden cursor-pointer touch-none"
                        onPointerDown={(e) => {
                            e.stopPropagation();
                            e.currentTarget.setPointerCapture(e.pointerId);
                            const rect = e.currentTarget.getBoundingClientRect();
                            const percent = Math.round(((e.clientX - rect.left) / rect.width) * 100);
                            onSpeedChange(Math.max(0, Math.min(100, percent)));
                        }}
                        onPointerMove={(e) => {
                            if (e.buttons > 0) {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const percent = Math.round(((e.clientX - rect.left) / rect.width) * 100);
                                onSpeedChange(Math.max(0, Math.min(100, percent)));
                            }
                        }}
                    >
                        <div 
                            className="h-full bg-accent-yellow rounded-full"
                            style={{ width: `${speedValue}%` }}
                        />
                    </div>
                    <span className="text-[8px] font-bold text-accent-yellow">{(1 + speedValue * 0.03).toFixed(1)}x</span>
                </motion.div>
            )}

            {/* Dark Mode Toggle - Clickable */}
            {slideId === 'darkmode' && (
                 <motion.div 
                    key="dark"
                    initial={{ rotateX: 90 }}
                    animate={{ rotateX: 0 }}
                    exit={{ opacity: 0 }}
                    className="bg-zinc-800 border border-zinc-700 shadow-xl px-4 py-2 rounded-full flex items-center gap-2 cursor-pointer"
                    onClick={onToggleDarkMode}
                >
                    <div className="w-6 h-6 rounded-full bg-black flex items-center justify-center text-white">
                        <Moon className="w-3 h-3 fill-current" />
                    </div>
                    <div className="flex-1">
                        <div className="text-white font-bold text-[10px]">Dark Mode</div>
                    </div>
                    <div className={cn(
                        "w-8 h-4 rounded-full p-0.5 transition-colors",
                        darkModeOn ? "bg-green-500" : "bg-gray-500"
                    )}>
                        <motion.div 
                            className="w-3 h-3 bg-white rounded-full shadow-sm"
                            animate={{ x: darkModeOn ? 16 : 0 }}
                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        />
                    </div>
                </motion.div>
            )}

            {/* Offline Badge - Unified style with Dark Mode */}
            {slideId === 'offline' && (
                 <motion.div 
                    key="offline"
                    initial={{ rotateX: 90 }}
                    animate={{ rotateX: 0 }}
                    exit={{ opacity: 0 }}
                    className="bg-zinc-800 border border-zinc-700 shadow-xl px-4 py-2 rounded-full flex items-center gap-2"
                >
                    <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                        <WifiOff className="w-3 h-3 text-green-400" />
                    </div>
                    <div className="flex-1">
                        <div className="text-white font-bold text-[10px]">Offline Mode</div>
                    </div>
                    <div className="w-8 h-4 rounded-full p-0.5 bg-green-500">
                        <motion.div 
                            className="w-3 h-3 bg-white rounded-full shadow-sm"
                            animate={{ x: 16 }}
                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        />
                    </div>
                </motion.div>
            )}

        </AnimatePresence>
    )
}
